<template>
    <v-card
        class="pokedex-card skin-pokemon-card"
        :class="{ 'pokedex-card--clickable': !unknown }"
        @click="onClick"
    >
        <div class="pokedex-card__header" v-if="!unknown">
            <span class="pokedex-card__name">{{ pokemonType.name }}</span>
            <v-progress-linear
                background-color="error"
                color="success"
                height="13"
                class="pokedex-card__hp-bar"
                :value="100"
            >
                <strong class="pokedex-card__hp-label">{{ pokemonType.baseHp }}</strong>
            </v-progress-linear>
        </div>

        <div class="pokedex-card__image-wrap">
            <v-img
                class="pokedex-card__image"
                :class="{
                    'unknown': unknown,
                    'pokedex-card__image--owned': !unknown && ownedCount > 0,
                    'pokedex-card__image--stolen': !unknown && ownedCount === 0 && stolenCount > 0,
                }"
                :src="getIcon()"
                contain
            />

            <TypePill
                v-if="!unknown"
                :type="pokemonType.type"
                class="pokedex-card__type-chip"
            >
                {{ pokemonType.type.id }}
            </TypePill>

            <v-chip
                v-if="!unknown && everCaught"
                color="amber darken-2"
                class="pokedex-card__catch-chip black--text chip-dekorator-dark"
            >
                {{ ownedCount }}
                <v-icon class="ml-1" color="black">mdi-pokeball</v-icon>
            </v-chip>
        </div>
    </v-card>
</template>

<script>
    import TypePill from "@/components/utils/TypePill";
    import {pokemonImageSrc} from '@/utils/pokemonImage';

    export default {
        name: 'PokedexCard',
        components: {TypePill},
        props: {
            pokemonType: {type: Object, required: true},
            unknown: {type: Boolean, default: false},
            // Aktualne vlastnene (nikoliv ukradene) kusy - zlate podsviceni,
            // cislo v badge.
            ownedCount: {type: Number, default: 0},
            // Aktualne ukradene kusy (Team Rocket) - stribrne podsviceni,
            // jen pokud ownedCount === 0 (viz claude/components/pokedex.md).
            stolenCount: {type: Number, default: 0},
            // "Nekdy chycen" (perzistentni, prezije i propusteni vsech kusu)
            // - viditelnost badge, nezavisle na ownedCount/stolenCount.
            everCaught: {type: Boolean, default: false},
        },
        methods: {
            getIcon() {
                return pokemonImageSrc(this.pokemonType.img);
            },
            onClick() {
                if (!this.unknown) {
                    this.$emit('open-skills');
                }
            },
        },
    }
</script>

<style scoped>
    .pokedex-card {
        height: 100%;
        display: flex;
        flex-direction: column;
        position: relative;
        overflow: hidden;
        container-type: inline-size;
    }

    .pokedex-card--clickable {
        cursor: pointer;
        transition: transform .12s ease, box-shadow .12s ease;
    }

    .pokedex-card--clickable:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 10px rgba(0, 0, 0, .35);
    }

    .pokedex-card__header {
        padding: 6px 8px 4px;
        text-align: center;
    }

    .pokedex-card__name {
        display: block;
        font-weight: 600;
        font-size: clamp(10.64px, 3.8px + 1.9vw, 15.2px);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .pokedex-card__hp-bar {
        margin-top: 4px;
        border-radius: 4px;
        height: clamp(8px, 6cqi, 14px) !important;
    }

    /* Stejny mekky halo za cislem jako .pd-hp-text v PokemonDetail.vue
       (box-shadow blur + stejnobarevne pozadi, theme-aware) - primo na
       <strong>, bez zvlastniho obalujiciho <div>, at nerozhodi centrovani
       v bare. */
    .pokedex-card__hp-label {
        display: inline-block;
        padding: 0 3px;
        border-radius: 40%;
        font-size: clamp(calc(.45rem + 2px), 4cqi, calc(.65rem + 2px));
    }

    .theme--light .pokedex-card__hp-label {
        box-shadow: 0 0 4px 6px rgba(255, 255, 255, .63);
        background-color: rgba(255, 255, 255, .63);
    }

    .theme--dark .pokedex-card__hp-label {
        box-shadow: 0 0 4px 6px rgba(0, 0, 0, .63);
        background-color: rgba(0, 0, 0, .63);
    }

    /* Gradient primo ve vyplni baru (ne halo za textem) - viz zadani
       uzivatele 2026-08-29. ::v-deep nutne, Vuetify renderuje
       .v-progress-linear__determinate mimo dosah scoped stylu teto
       komponenty. !important potreba, Vuetify utilitni trida "success"
       (color prop) uz sama pouziva !important na background-color. */
    ::v-deep .pokedex-card__hp-bar .v-progress-linear__determinate {
        background: linear-gradient(to bottom, #8bd66a 0%, #4caf50 55%, #388e3c 100%) !important;
    }

    .pokedex-card__type-chip {
        position: absolute;
        left: 6px;
        bottom: 6px;
        z-index: 1;
        font-weight: 600;
        padding: 0 clamp(3px, 3cqi, 6px);
    }

    /* Kombinace trid kvuli specificite - viz claude/components/pokedex.md. */
    .pokedex-card__type-chip.v-chip.v-size--default {
        height: clamp(16.8px, 6px + 3vw, 24px);
        font-size: clamp(9.8px, 3.5px + 1.75vw, 14px);
    }

    .pokedex-card__image--owned {
        filter:
            drop-shadow(0 0 6px rgba(255, 200, 0, .9))
            drop-shadow(0 0 16px rgba(255, 200, 0, .55));
    }

    /* Kovove "stribro" - skoro-bile jadro (chladny odstin, ne cisty bily,
       aby to necetlo jako obycejny highlight) + sedava stredni vrstva +
       slabounky modrosedy okraj, misto ploche modre - ta proti svetlemu
       pozadi karty pusobila mnohem slaběji nez zluta u --owned (lidske oko
       je na zlutou/oranzovou citlivejsi), ale ciste odsedivena zase
       splyvala s pozadim. Vic vrstev = kovovy "lesk" mista jedne barvy.

       Dve varianty podle tematu (viz $vuetify.theme.dark v App.vue) -
       stejne svetle "stribro" jako na tmavem pozadi by na svetlem pozadi
       karty (.theme--light) melo malo kontrastu, proto tam tmavsi/sytejsi
       odstin stejne kompozice vrstev. */
    .theme--dark .pokedex-card__image--stolen {
        filter:
            drop-shadow(0 0 3px rgba(200, 205, 212, 1))
            drop-shadow(0 0 9px rgba(165, 173, 184, .9))
            drop-shadow(0 0 18px rgba(125, 140, 160, .6));
    }

    .theme--light .pokedex-card__image--stolen {
        filter:
            drop-shadow(0 0 3px rgba(120, 130, 145, 1))
            drop-shadow(0 0 9px rgba(90, 100, 118, .85))
            drop-shadow(0 0 18px rgba(60, 75, 95, .55));
    }

    .pokedex-card__catch-chip {
        position: absolute;
        right: 6px;
        bottom: 6px;
        z-index: 1;
        font-weight: 600;
        padding: 0 clamp(3px, 3cqi, 6px);
    }

    /* Stejne clamp hodnoty jako .pokedex-card__type-chip - vizualne sourode
       pilulky v protilehlych rozich karty. */
    .pokedex-card__catch-chip.v-chip.v-size--default {
        height: clamp(16.8px, 6px + 3vw, 24px);
        font-size: clamp(9.8px, 3.5px + 1.75vw, 14px);
    }

    .pokedex-card__catch-chip .v-icon {
        font-size: clamp(11px, 4px + 2vw, 16px);
    }

    .pokedex-card__image-wrap {
        position: relative;
        flex: 1 1 auto;
        min-height: 90px;
        display: flex;
    }

    .pokedex-card__image {
        flex: 1 1 auto;
    }
</style>
